import { Injectable, inject, signal, computed } from '@angular/core';
import {
  getPermissionName,
  getAllPermissionNames,
  combinePermissions,
} from './permission.constants';

/**
 * Permission Service
 * 
 * Manages user permission bitmask using signals and provides
 * methods for checking permissions using bitwise operations.
 * Replicates the logic from C# PermissionChecker.cs
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  // Private signal for user permission mask
  private readonly _userMask = signal<bigint | null>(null);
  
  // Private signal for loading state
  private readonly _isLoading = signal<boolean>(false);

  // Public readonly signal for component consumption
  readonly userMask = this._userMask.asReadonly();
  
  // Public readonly signal for loading state
  readonly isLoading = this._isLoading.asReadonly();

  // Computed signal to check if user has any permissions
  readonly hasPermissions = computed(() => {
    const mask = this._userMask();
    return mask !== null && mask !== 0n;
  });

  /**
   * Checks if the granted permissions include the required permission
   * Uses bitwise AND to verify the permission is present
   * 
   * @param required The permission being checked
   * @returns True if the user has the required permission
   */
  hasPermission(required: bigint): boolean {
    const granted = this._userMask();
    if (granted === null) {
      return false;
    }
    return (granted & required) === required;
  }

  /**
   * Checks if the granted permissions include ANY of the specified permissions
   * 
   * @param anyOf Array of permissions to check against
   * @returns True if the user has at least one of the specified permissions
   */
  hasAnyPermission(...anyOf: bigint[]): boolean {
    if (!anyOf || anyOf.length === 0) {
      return false;
    }

    const granted = this._userMask();
    if (granted === null) {
      return false;
    }

    return anyOf.some(permission => (granted & permission) === permission);
  }

  /**
   * Checks if the granted permissions include ALL of the specified permissions
   * 
   * @param allOf Array of permissions to check against
   * @returns True if the user has all of the specified permissions
   */
  hasAllPermissions(...allOf: bigint[]): boolean {
    if (!allOf || allOf.length === 0) {
      return true;
    }

    const granted = this._userMask();
    if (granted === null) {
      return false;
    }

    return allOf.every(permission => (granted & permission) === permission);
  }

  /**
   * Checks if the granted permissions include NONE of the specified permissions
   * 
   * @param noneOf Array of permissions that should NOT be present
   * @returns True if the user has none of the specified permissions
   */
  hasNoneOfPermissions(...noneOf: bigint[]): boolean {
    if (!noneOf || noneOf.length === 0) {
      return true;
    }

    const granted = this._userMask();
    if (granted === null) {
      return true;
    }

    return !noneOf.some(permission => (granted & permission) === permission);
  }

  /**
   * Loads permissions from a bitmask string, number, or BigInt
   * Converts the input to BigInt and stores it in the signal
   * 
   * @param mask The permission bitmask as string, number, or BigInt
   */
  loadPermissions(mask: string | number | bigint ): void {
    this._isLoading.set(true);
    try {
      let permissionMask: bigint;
      if (typeof mask === 'string') {
        // Handle empty or invalid strings
        if (!mask || mask.trim() === '') {
          permissionMask = 0n;
        } else {
          permissionMask = BigInt(mask);
        }
      } else if (typeof mask === 'number') {
        permissionMask = BigInt(mask);
      } else {
        permissionMask = mask;
      }

      this._userMask.set(permissionMask);
    } catch (error) {
      // If conversion fails, set to 0n (no permissions)
      console.error('Failed to load permissions:', error);
      this._userMask.set(0n);
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Clears user permissions
   */
  clearPermissions(): void {
    this._isLoading.set(false);
    this._userMask.set(null);
  }

  /**
   * Gets a list of permission names from the current bitmask
   * 
   * @returns List of permission names
   */
  getPermissionNames(): string[] {
    const permissionMask = this._userMask();
    if (permissionMask === null || permissionMask === 0n) {
      return [];
    }

    const permissions: string[] = [];

    // Check first 300 bits (we have permissions up to bit index 231)
    for (let i = 0; i < 300; i++) {
      const bitMask = 1n << BigInt(i);
      if ((permissionMask & bitMask) === bitMask) {
        const name = getPermissionName(i);
        if (name !== null) {
          permissions.push(name);
        }
      }
    }

    return permissions;
  }

  /**
   * Gets the count of permissions in the current bitmask
   * 
   * @returns Number of permissions enabled
   */
  getPermissionCount(): number {
    const permissionMask = this._userMask();
    if (permissionMask === null || permissionMask === 0n) {
      return 0;
    }

    let count = 0;

    // Check first 300 bits
    for (let i = 0; i < 300; i++) {
      const bitMask = 1n << BigInt(i);
      if ((permissionMask & bitMask) === bitMask) {
        count++;
      }
    }

    return count;
  }

  /**
   * Checks if the permission mask is empty (no permissions)
   * 
   * @returns True if no permissions are set
   */
  isEmpty(): boolean {
    const permissionMask = this._userMask();
    return permissionMask === null || permissionMask === 0n;
  }

  /**
   * Adds a permission to the existing permission bitmask
   * 
   * @param toAdd The permission to add
   * @returns Updated bitmask with the new permission
   */
  addPermission(toAdd: bigint): bigint {
    const granted = this._userMask() ?? 0n;
    const updated = granted | toAdd;
    this._userMask.set(updated);
    return updated;
  }

  /**
   * Removes a permission from the existing permission bitmask
   * 
   * @param toRemove The permission to remove
   * @returns Updated bitmask without the specified permission
   */
  removePermission(toRemove: bigint): bigint {
    const granted = this._userMask() ?? 0n;
    const updated = granted & ~toRemove;
    this._userMask.set(updated);
    return updated;
  }

  /**
   * Toggles a permission in the existing permission bitmask
   * 
   * @param toToggle The permission to toggle
   * @returns Updated bitmask with the permission toggled
   */
  togglePermission(toToggle: bigint): bigint {
    const granted = this._userMask() ?? 0n;
    const updated = granted ^ toToggle;
    this._userMask.set(updated);
    return updated;
  }

  /**
   * Merges multiple permission masks into one
   * 
   * @param masks Array of permission masks to merge
   * @returns Combined permission mask
   */
  mergePermissions(...masks: bigint[]): bigint {
    return combinePermissions(...masks);
  }
}

